import type { ReactNode } from "react";

export type AuthStep = "identify" | "verify-primary" | "name" | "second-auth";

export type StatItem = { label: string; value: string; accent?: string };

export type LeftContent = {
  leftTitle: ReactNode;
  leftDescription: string;
  leftStats?: StatItem[];
  hideHomeLink?: boolean;
  /** Destination for the top-right back link (defaults to "/"). */
  homeHref?: string;
  /** Label for the top-right back link (defaults to "← Home"). */
  homeLabel?: string;
};

export function getLeftContent(step: AuthStep): LeftContent {
  const isOnboarding = step === "name" || step === "second-auth";

  return {
    leftTitle: isOnboarding ? (
      <>
        Almost there.
        <br />
        Let&apos;s set up your <em className="italic">profile.</em>
      </>
    ) : (
      <>
        Welcome
        <br />
        to <em className="italic">EZSpot.</em>
      </>
    ),
    leftDescription: isOnboarding
      ? "Just a few details so we can personalize your EZSpot experience."
      : "Your favorite spots are waiting. Sign in and get parked in seconds.",
    hideHomeLink: isOnboarding,
  };
}

export function getContextOverride(
  context: string | null | undefined,
): Partial<LeftContent> | null {
  if (context === "become-host") {
    return {
      leftTitle: (
        <>
          Your spot.
          <br />
          Your <em className="italic">passive income.</em>
        </>
      ),
      leftDescription:
        "Join active hosts already earning on EZSpot. Set up your listing in under 5 minutes — no experience needed.",
      leftStats: [{ label: "Avg setup", value: "5", accent: "min" }],
    };
  }
  if (context === "refer") {
    // Keep the default welcome messaging; only redirect the back link to the
    // Refer & Earn page the user came from.
    return {
      homeHref: "/refer",
      homeLabel: "← Refer & Earn",
    };
  }
  return null;
}

/**
 * Left panel for a visitor who picked an address on the host landing page
 * before signing in. Names their spot once, in the title, in place of the
 * generic become-host pitch.
 */
export function getHostAddressContent(
  streetAddress: string,
): Partial<LeftContent> {
  return {
    leftTitle: (
      <>
        {streetAddress} is <em className="italic">ready to list.</em>
      </>
    ),
    leftDescription: "Sign in or create an account to finish your listing.",
    leftStats: [
      { label: "Avg setup", value: "5", accent: "min" },
      { label: "To list", value: "Free" },
    ],
  };
}
